import constate from 'constate'
import { useCallback, useEffect, useRef, useState } from 'react'
import { TAuthData } from '../definitions/auth'
import {
  clearKeycloakSession,
  initKeycloak,
  isKeycloakConfigured,
  KEYCLOAK_REFRESH_INTERVAL_MS,
  loginWithKeycloak,
  logoutFromKeycloak,
  mapKeycloakToAuthData,
  refreshKeycloakToken,
  resolveRedirectUri
} from '../services/keycloak'

export const AUTH_LOCAL_STORAGE_KEY = 'user'
export const SKIP_AUTO_LOGIN_ONCE_KEY = 'skip_auto_login_once'
const AUTH_SYNC_KEY = 'auth_sync_event'

const emitAuthEvent = (eventType: 'login' | 'logout') => {
  try {
    localStorage.setItem(
      AUTH_SYNC_KEY,
      JSON.stringify({
        eventType,
        timestamp: Date.now()
      })
    )
  } catch {}
}

const clearLegacyAuthStorage = () => {
  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch {}
}

const markSkipAutoLoginOnce = () => {
  try {
    sessionStorage.setItem(SKIP_AUTO_LOGIN_ONCE_KEY, '1')
  } catch {}
}

const useAuth = () => {
  const [authData, setAuthDataState] = useState<TAuthData | undefined>()
  const [isAuthInitialized, setIsAuthInitialized] = useState(false)
  const refreshPromiseRef = useRef<Promise<string | undefined> | null>(null)

  const syncFromKeycloak = useCallback(() => {
    const nextAuthData = mapKeycloakToAuthData()
    setAuthDataState(nextAuthData)
    if (!nextAuthData) clearLegacyAuthStorage()
    return nextAuthData
  }, [])

  const clearAuthState = useCallback(() => {
    setAuthDataState(undefined)
    clearLegacyAuthStorage()
  }, [])

  const refreshAccessToken = useCallback(async (minValidity = 30): Promise<string | undefined> => {
    if (!isKeycloakConfigured()) return authData?.access_token

    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    refreshPromiseRef.current = (async () => {
      const updatedToken = await refreshKeycloakToken(minValidity)
      if (!updatedToken) {
        clearAuthState()
        return undefined
      }
      syncFromKeycloak()
      return updatedToken
    })().finally(() => {
      refreshPromiseRef.current = null
    })

    return refreshPromiseRef.current
  }, [authData?.access_token, clearAuthState, syncFromKeycloak])

  const login = useCallback(async (path?: string) => {
    if (!isKeycloakConfigured()) {
      throw new Error('Keycloak is not configured. Set REACT_APP_KEYCLOAK_URL/REALM/CLIENT_ID.')
    }

    emitAuthEvent('login')
    await loginWithKeycloak(resolveRedirectUri(path))
  }, [])

  const logout = useCallback(async (path?: string) => {
    markSkipAutoLoginOnce()
    clearAuthState()
    emitAuthEvent('logout')

    if (isKeycloakConfigured()) {
      logoutFromKeycloak(resolveRedirectUri(path))
      return
    }

    clearKeycloakSession()
  }, [clearAuthState])

  const setAuthData = useCallback((data: TAuthData | undefined) => {
    setAuthDataState(data)
  }, [])

  const removeAuthData = useCallback(async () => {
    clearKeycloakSession()
    clearAuthState()
    emitAuthEvent('logout')
  }, [clearAuthState])

  useEffect(() => {
    let isMounted = true

    const initialize = async () => {
      clearLegacyAuthStorage()

      if (!isKeycloakConfigured()) {
        if (isMounted) setIsAuthInitialized(true)
        return
      }

      try {
        const isAuthenticated = await initKeycloak()
        if (!isMounted) return

        if (isAuthenticated) {
          syncFromKeycloak()
        } else {
          clearAuthState()
        }
      } catch {
        if (isMounted) clearAuthState()
      } finally {
        if (isMounted) setIsAuthInitialized(true)
      }
    }

    initialize().catch(() => {
      if (isMounted) {
        clearAuthState()
        setIsAuthInitialized(true)
      }
    })
    return () => {
      isMounted = false
    }
  }, [clearAuthState, syncFromKeycloak])

  useEffect(() => {
    if (!isAuthInitialized || !isKeycloakConfigured()) return

    const interval = window.setInterval(() => {
      refreshAccessToken(60).catch(() => {
        clearKeycloakSession()
        clearAuthState()
      })
    }, KEYCLOAK_REFRESH_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [isAuthInitialized, refreshAccessToken, clearAuthState])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_SYNC_KEY || !event.newValue) return

      try {
        const { eventType } = JSON.parse(event.newValue) as { eventType: 'login' | 'logout' }
        if (eventType === 'logout') {
          clearKeycloakSession()
          clearAuthState()
          return
        }

        syncFromKeycloak()
      } catch {}
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [clearAuthState, syncFromKeycloak])

  return {
    authData,
    setAuthData,
    removeAuthData,
    isAuthInitialized,
    isKeycloakConfigured: isKeycloakConfigured(),
    refreshAccessToken,
    login,
    logout
  }
}

export const [AuthProvider, useAuthContext] = constate(useAuth)
